import { generateCodeVerifier, OAuth2Client } from '@badgateway/oauth2-client';

export const VERIFIER_STORAGE = 'tinct-oauth-verifier';
export const STATE_STORAGE = 'tinct-oauth-state';
/** Where the verifier was kept before it got its own name; cleared when a login starts. */
const LEGACY_VERIFIER_STORAGE = 'auth-store';

const oauthClient = new OAuth2Client({
  server: `${import.meta.env["VITE_API_URL"]}`,
  clientId: `${import.meta.env["VITE_OAUTH_CLIENT_ID"]}`,
  tokenEndpoint: '/oauth2/token',
  authorizationEndpoint: '/oauth2/authorize',
});

/** 32 random bytes, base64url-encoded: unguessable, and safe in a query string as is. */
const generateState = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const getAuthorizationUrl = async (extraParams?: Record<string, string>) => {
  const codeVerifier = await generateCodeVerifier();
  // The state ties the callback to the authorization this browser started (CSRF protection on
  // /login/callback). It is kept beside the PKCE verifier, so a login started in another tab
  // replaces both together.
  const state = generateState();
  localStorage.removeItem(LEGACY_VERIFIER_STORAGE);
  localStorage.setItem(VERIFIER_STORAGE, codeVerifier);
  localStorage.setItem(STATE_STORAGE, state);
  return oauthClient.authorizationCode.getAuthorizeUri({
    redirectUri: `${window.location.origin}/login/callback`,
    codeVerifier,
    state,
    scope: ['openid', 'offline_access'],
    extraParams
  })
};


const getTokensFromCodeRedirect = async (url: string) => {
  const codeVerifier = localStorage.getItem(VERIFIER_STORAGE);
  if(!codeVerifier) {
    throw new Error('Code verifier not found in local storage. Please initiate the OAuth flow first.');
  }
  // Compared here rather than left to the library: it skips the check when no state is stored, and
  // its error message quotes the expected value, which the callback page logs.
  const state = localStorage.getItem(STATE_STORAGE);
  if (!state || new URL(url).searchParams.get('state') !== state) {
    throw new Error('The OAuth state of the callback does not match the authorization this browser started.');
  }

  const tokens = await oauthClient.authorizationCode.getTokenFromCodeRedirect(url, {
    redirectUri: `${window.location.origin}/login/callback`,
    codeVerifier,
    state,
  });
  // Single use: once exchanged, no later callback may match this authorization. After a failure
  // they stay, and the callback page's retry starts a fresh authorization that replaces both.
  localStorage.removeItem(STATE_STORAGE);
  localStorage.removeItem(VERIFIER_STORAGE);
  return tokens;
}

const getNewToken = async (refreshToken: string) => {
  return oauthClient.refreshToken({
    refreshToken: refreshToken,
    accessToken: "",
    expiresAt: null
  });
}


export {
  getAuthorizationUrl,
  getTokensFromCodeRedirect,
  getNewToken
}
